// src/js/cindy/round-entry.ts
//
// P-18 shared row-repeater — the DOM-building half of the Shared Lap-Entry Component
// (263-UI-SPEC.md "Component Specification — Shared Lap-Entry Component"). Both
// `src/js/workouts.ts` (263-10, the /workouts/new and /workouts/<id> page form) and
// `src/js/home/cindy-modal.ts` (263-11, the sixth home-dashboard modal) import
// `mountRoundEntry()` from `src/js/cindy/entry-form.ts` — neither reimplements the row markup,
// renumbering or delegated-listener wiring below. Generalizes gym-modal.ts's
// `renderGymCard()`/`populateGymCard()` build-from-scratch / populate-in-place pair
// (src/js/home/gym-modal.ts:214-322) to a plain duration-string row instead of a gym card.
//
// Deliberately imports nothing from ../home/ or ../workouts — a "shared" component that pulled
// in either surface's dependency tree would defeat the whole point of sharing it (P-18/T-263-12).

/** Public controller returned by mountRoundEntry() — frozen in 263-05-PLAN.md's interface_context. */
export interface RoundEntryController {
    getRounds(): string[];
    setRounds(values: string[]): void;
    addRound(value?: string): void;
    clear(): void;
    onChange(cb: () => void): void;
}

// Module-level constant SVG string — Bootstrap Icons trash glyph, UI-SPEC's Row-repeater
// contract, verbatim. Assigned via innerHTML ONCE per row as a static constant carrying zero
// interpolated/user-derived text (T-263-16) — never built from a round value, index or any
// pasted input.
const TRASH_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">' +
    '<path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>' +
    '<path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1z"/>' +
    '</svg>';

/** Empties an element's children without innerHTML (T-263-16 — no interpolated-value innerHTML assignment). */
function clearChildren(el: HTMLElement): void {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}

/** Builds one round row from scratch — UI-SPEC's Row-repeater contract, verbatim markup shape. */
function buildRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'd-flex align-items-center gap-2 mb-2 cindy-round-row';

    const badge = document.createElement('span');
    badge.className = 'badge text-bg-secondary cindy-round-badge';
    row.appendChild(badge);

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'form-control cindy-round-duration';
    input.placeholder = 'MM:SS.hh';
    input.setAttribute('inputmode', 'decimal');
    // autocomplete="off" is mandatory, not cosmetic, on every server-state-backed control this
    // phase introduces — without it a browser reload-restore can resurrect a stale row value
    // that disagrees with the persisted session.
    input.autocomplete = 'off';
    row.appendChild(input);

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn btn-sm btn-link text-danger p-0 cindy-round-delete';
    deleteBtn.innerHTML = TRASH_ICON_SVG;
    row.appendChild(deleteBtn);

    return row;
}

/**
 * Renumber every row positionally — `R{n}` badge text, `data-round-no` and the delete button's
 * `aria-label` are NEVER stored data of their own; they are always derived from current DOM
 * order after any add or remove (UI-SPEC's Row-repeater contract: "auto-numbered, not editable").
 */
function renumber(listEl: HTMLElement): void {
    const rows = Array.from(listEl.querySelectorAll<HTMLElement>('.cindy-round-row'));
    rows.forEach((row, idx) => {
        const n = idx + 1;
        row.dataset.roundNo = String(n);
        const badge = row.querySelector('.cindy-round-badge');
        if (badge) badge.textContent = 'R' + n;
        const deleteBtn = row.querySelector('.cindy-round-delete');
        if (deleteBtn) deleteBtn.setAttribute('aria-label', 'Remove round ' + n);
    });
}

/**
 * Mounts the shared row-repeater into `listEl`. Both `mountCindyEntry()` (entry-form.ts) and
 * anything else that needs a bare row-repeater (none today — entry-form.ts is the only caller)
 * use this directly; the row markup, renumbering and delegated-listener wiring below is the
 * SINGLE implementation, per P-18/T-263-12.
 */
export function mountRoundEntry(listEl: HTMLElement, initial?: string[]): RoundEntryController {
    const changeCbs: (() => void)[] = [];
    const fireChange = (): void => {
        changeCbs.forEach((cb) => cb());
    };

    // ONE delegated listener per event type on listEl (never per-row) — rows added later via
    // addRound()/setRounds() are covered automatically without any rebinding step.
    listEl.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('cindy-round-duration')) {
            fireChange();
        }
    });

    listEl.addEventListener('click', (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const deleteBtn = target.closest('.cindy-round-delete') as HTMLElement | null;
        if (!deleteBtn) return;
        const row = deleteBtn.closest('.cindy-round-row') as HTMLElement | null;
        if (!row) return;
        // No confirmation on row delete — P-05 / gym-modal's own pre-save-form-state convention
        // (these rows are unsaved form state, not yet persisted).
        row.remove();
        renumber(listEl);
        fireChange();
    });

    if (initial && initial.length > 0) {
        initial.forEach((value) => {
            const row = buildRow();
            (row.querySelector('.cindy-round-duration') as HTMLInputElement).value = value;
            listEl.appendChild(row);
        });
        renumber(listEl);
    }

    return {
        getRounds(): string[] {
            return Array.from(listEl.querySelectorAll<HTMLInputElement>('.cindy-round-duration')).map(
                (input) => input.value
            );
        },

        setRounds(values: string[]): void {
            const existingRows = Array.from(listEl.querySelectorAll<HTMLElement>('.cindy-round-row'));
            if (existingRows.length === values.length) {
                // Update in place rather than destroy/recreate — a rebuild would destroy focus
                // that may already have landed inside a row (gym-modal's populate-in-place
                // rule, generalized to this repeater).
                existingRows.forEach((row, idx) => {
                    const input = row.querySelector('.cindy-round-duration') as HTMLInputElement | null;
                    if (input) input.value = values[idx];
                });
            } else {
                clearChildren(listEl);
                values.forEach((value) => {
                    const row = buildRow();
                    (row.querySelector('.cindy-round-duration') as HTMLInputElement).value = value;
                    listEl.appendChild(row);
                });
                renumber(listEl);
            }
            fireChange();
        },

        addRound(value?: string): void {
            const row = buildRow();
            if (value !== undefined) {
                (row.querySelector('.cindy-round-duration') as HTMLInputElement).value = value;
            }
            listEl.appendChild(row);
            renumber(listEl);
            fireChange();
        },

        clear(): void {
            clearChildren(listEl);
            fireChange();
        },

        onChange(cb: () => void): void {
            changeCbs.push(cb);
        },
    };
}
